/**
 * ============================================================
 * Style Playground — Auto-generated metadata
 * ============================================================
 * Device: desktop
 * Language: English
 *
 * ## Style Direction (from analysis)
 * <style-name>ArchitecturalGrid</style-name>
 * **Mode:** Light
 * 
 * **Style Recipe:**
 * - **Core Visual Style:** Outlined / Wireframe
 * - **Layout Structure:** Strict Grid / Swiss
 * - **Visual Complexity:** Minimalist (Invisible)
 * 
 * **Implementation Techniques:**
 * - **Material & Surfaces:** Pure transparent surfaces. Zero shadows. The UI relies entirely on lines and typography to define hierarchy.
 * - **Border-Radius:** Sharp (0px) or exceptionally tight (2px) corners. Everything feels drafted, precise, and architectural.
 * - **Typography:** Swiss-style typography. Use a neo-grotesque font (e.g., Helvetica Now, Neue Haas Grotesk) with tight tracking. Prominent use of all-caps small-text for metadata and labels.
 * - **Lighting & Shadows:** Absolute absence of depth. Flat, 2D space.
 * - **Border Treatments:** The defining feature. 1px solid lines spanning the entire width/height of the viewport (`border-right`, `border-bottom` on grid items) in a medium-light silver/gray. 
 * 
 * **Color Direction:**
 * Monochromatic and stark. Pure white background (`#FFFFFF`) with pitch-black (`#000000`) typography. The primary "color" is just a subtle, muted accent like a dusty Sage Green or muted Terracotta, used extremely sparingly for active tabs or token selections. This approach makes the user's component previews the only truly colorful elements on the screen.
 * ============================================================
 */

import React, { useState } from 'react';
import { Icon } from '@iconify/react';

export default function ComponentDetail() {
  const [activeTab, setActiveTab] = useState('Design');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const tabs = ['Design', 'Code', 'Props', 'Guidelines'];

  const propsData = [
    { name: 'variant', type: "'primary' | 'secondary' | 'danger'", default: "'primary'", desc: 'Defines the visual style of the button.' },
    { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", desc: 'Controls the physical size and padding.' },
    { name: 'disabled', type: 'boolean', default: 'false', desc: 'Prevents user interaction.' },
    { name: 'onClick', type: '(e: React.MouseEvent) => void', default: 'undefined', desc: 'Fired when the button is clicked.' },
  ];

  return (
    <div className="flex h-screen w-full bg-white text-black font-sans selection:bg-stone-200">
      
      {/* Sidebar - Strict Grid */}
      <aside className="w-64 flex-shrink-0 flex flex-col border-r border-stone-300">
        <div className="h-16 flex items-center px-6 border-b border-stone-300">
          <div className="flex items-center gap-2 font-medium tracking-tight">
            <Icon icon="lucide:layers" className="w-5 h-5" />
            <span className="text-sm uppercase tracking-widest">ComponentHub</span>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto py-6 flex flex-col gap-8">
          <div className="px-6 flex flex-col gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-2">Library</span>
            <button className="flex items-center gap-3 text-sm text-stone-600 hover:text-black transition-colors py-1">
              <Icon icon="lucide:layout-dashboard" className="w-4 h-4" />
              Dashboard
            </button>
            <button className="flex items-center gap-3 text-sm font-medium text-black py-1">
              <Icon icon="lucide:component" className="w-4 h-4" />
              Components
            </button>
            <button className="flex items-center gap-3 text-sm text-stone-600 hover:text-black transition-colors py-1">
              <Icon icon="lucide:palette" className="w-4 h-4" />
              Tokens
            </button>
          </div>

          <div className="px-6 flex flex-col gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-2">System</span>
            <button className="flex items-center gap-3 text-sm text-stone-600 hover:text-black transition-colors py-1">
              <Icon icon="lucide:history" className="w-4 h-4" />
              Changelog
            </button>
            <button className="flex items-center gap-3 text-sm text-stone-600 hover:text-black transition-colors py-1">
              <Icon icon="lucide:settings" className="w-4 h-4" />
              Settings
            </button>
          </div>
        </div>
        
        <div className="p-6 border-t border-stone-300">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-stone-200 rounded-sm flex items-center justify-center">
              <span className="text-xs font-medium">JD</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-medium">Jane Doe</span>
              <span className="text-[10px] text-stone-500">Design Lead</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 bg-white">
        
        {/* Header */}
        <header className="h-16 flex-shrink-0 flex items-center justify-between px-8 border-b border-stone-300">
          <div className="flex items-center gap-4">
            <button className="p-1.5 text-stone-500 hover:text-black hover:bg-stone-100 rounded-sm transition-colors">
              <Icon icon="lucide:arrow-left" className="w-5 h-5" />
            </button>
            <div className="h-6 w-px bg-stone-300"></div>
            <h1 className="text-lg font-medium tracking-tight">Primary Button</h1>
            <div className="flex items-center gap-2 ml-4">
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider border border-stone-300 text-stone-600 rounded-sm">
                Published
              </span>
              <span className="text-xs font-mono text-stone-500">v1.2.0</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-1.5 text-sm border border-stone-300 hover:bg-stone-100 transition-colors rounded-sm">
              <Icon icon="lucide:history" className="w-4 h-4" />
              Versions
            </button>
            <button className="flex items-center gap-2 px-4 py-1.5 text-sm bg-black text-white hover:bg-stone-800 transition-colors rounded-sm">
              <Icon icon="lucide:play" className="w-4 h-4" />
              Preview
            </button>
            <div className="relative">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-1.5 border border-stone-300 hover:bg-stone-100 transition-colors rounded-sm"
              >
                <Icon icon="lucide:more-horizontal" className="w-5 h-5" />
              </button>
              {isMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-stone-300 shadow-none z-10">
                  <button className="w-full text-left px-4 py-2 text-sm hover:bg-stone-100">Edit Component</button>
                  <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">Archive</button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex-shrink-0 border-b border-stone-300 px-8 flex">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-4 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === tab 
                  ? 'border-black text-black' 
                  : 'border-transparent text-stone-500 hover:text-black'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-auto bg-stone-50/50 p-8">
          <div className="max-w-5xl mx-auto h-full flex flex-col">
            
            {activeTab === 'Props' && (
              <div className="border border-stone-300 bg-white flex-1 flex flex-col">
                <div className="grid grid-cols-12 border-b border-stone-300 bg-stone-50">
                  <div className="col-span-3 p-4 text-[10px] uppercase tracking-widest text-stone-500 font-medium border-r border-stone-300">Name</div>
                  <div className="col-span-4 p-4 text-[10px] uppercase tracking-widest text-stone-500 font-medium border-r border-stone-300">Type</div>
                  <div className="col-span-2 p-4 text-[10px] uppercase tracking-widest text-stone-500 font-medium border-r border-stone-300">Default</div>
                  <div className="col-span-3 p-4 text-[10px] uppercase tracking-widest text-stone-500 font-medium">Description</div>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {propsData.map((prop, idx) => (
                    <div key={idx} className="grid grid-cols-12 border-b border-stone-200 last:border-b-0 hover:bg-stone-50 transition-colors">
                      <div className="col-span-3 p-4 border-r border-stone-200 flex items-center">
                        <span className="font-mono text-sm text-[#8f9a6e]">{prop.name}</span>
                        {prop.name === 'variant' && <span className="ml-2 text-red-500 text-xs">*</span>}
                      </div>
                      <div className="col-span-4 p-4 border-r border-stone-200 flex items-center">
                        <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded-sm font-mono text-stone-600">{prop.type}</code>
                      </div>
                      <div className="col-span-2 p-4 border-r border-stone-200 flex items-center">
                        <span className="text-xs font-mono text-stone-500">{prop.default}</span>
                      </div>
                      <div className="col-span-3 p-4 flex items-center">
                        <span className="text-sm text-stone-600 leading-snug">{prop.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'Design' && (
              <div className="flex-1 border border-stone-300 bg-white flex items-center justify-center p-12 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]">
                <div className="flex flex-col gap-8 items-center bg-white p-12 border border-stone-200 shadow-sm">
                  <div className="flex gap-4">
                    <button className="px-6 py-2.5 bg-blue-600 text-white rounded-md font-medium text-sm">Primary Action</button>
                    <button className="px-6 py-2.5 bg-white border border-stone-300 text-stone-700 rounded-md font-medium text-sm hover:bg-stone-50">Secondary Action</button>
                  </div>
                  <div className="flex gap-4">
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium text-xs">Small</button>
                    <button className="px-6 py-2.5 bg-blue-600 text-white rounded-md font-medium text-sm">Medium</button>
                    <button className="px-8 py-3 bg-blue-600 text-white rounded-md font-medium text-base">Large</button>
                  </div>
                  <div className="flex gap-4">
                    <button className="px-6 py-2.5 bg-red-600 text-white rounded-md font-medium text-sm">Danger Action</button>
                    <button className="px-6 py-2.5 bg-stone-100 text-stone-400 rounded-md font-medium text-sm cursor-not-allowed">Disabled</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'Code' && (
              <div className="flex-1 border border-stone-300 bg-[#0d0d0d] text-stone-300 p-6 font-mono text-sm overflow-auto">
<pre><code>{`import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        danger: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 py-2',
        lg: 'h-10 px-8',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);`}</code></pre>
              </div>
            )}

            {activeTab === 'Guidelines' && (
              <div className="flex-1 border border-stone-300 bg-white p-12 overflow-auto max-w-3xl">
                <div className="prose prose-stone">
                  <h2 className="text-xl font-medium mb-4">Usage Guidelines</h2>
                  <p className="text-stone-600 mb-6 leading-relaxed">
                    The Primary Button is used to draw attention to the most important action on a page. 
                    There should generally only be one Primary Button per view to avoid confusing the user.
                  </p>
                  
                  <h3 className="text-sm uppercase tracking-widest text-stone-500 font-medium mb-3 mt-8">Do's & Don'ts</h3>
                  <div className="grid grid-cols-2 gap-8">
                    <div className="border border-green-200 p-4 bg-green-50/30">
                      <div className="flex items-center gap-2 text-green-700 font-medium mb-2 text-sm">
                        <Icon icon="lucide:check-circle" className="w-4 h-4" />
                        Do
                      </div>
                      <p className="text-sm text-stone-600">Use clear, action-oriented verbs (e.g., "Save", "Submit", "Create").</p>
                    </div>
                    <div className="border border-red-200 p-4 bg-red-50/30">
                      <div className="flex items-center gap-2 text-red-700 font-medium mb-2 text-sm">
                        <Icon icon="lucide:x-circle" className="w-4 h-4" />
                        Don't
                      </div>
                      <p className="text-sm text-stone-600">Use multiple primary buttons in the same container or form.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </main>
    </div>
  );
}
